// src/components/UserPage.js

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import UserDashboardChart from './UserDashboardChart';
import '../css/UserPage.css'; // Import the CSS file for styling
import ReportForm from './ReportForm';
import UserReports from './UserReports';
import Inbox from './Inbox'; // Import Inbox component for the user
import SendMessage from './SendMessage'; // Import SendMessage component
import CalendarView from './CalendarView';

import '@fortawesome/fontawesome-free/css/all.min.css';
import {
    faCoffee,
    faLaptop,
    faMobileAlt,
    faBook,
    faTv,
    faSignOutAlt,
    faClipboardList,
    faBars,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import InventoryCheck from './InventoryCheck'; // Import InventoryCheck component
import Orders from './Orders'; // Import Orders component
import { jwtDecode } from 'jwt-decode'; // Use named import
const UserPage = () => {
    const [view, setView] = useState('dashboard');
    const [categories, setCategories] = useState([]);
    const [products, setProducts] = useState([]);
    const [productName, setProductName] = useState('');
    const [productDescription, setProductDescription] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [attributesList, setAttributesList] = useState([]);
    const [message, setMessage] = useState('');
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [imageFile, setImageFile] = useState(null);
    const [imageUrl, setImageUrl] = useState('');
    const [isAdmin, setIsAdmin] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(true); // State for sidebar visibility
    const navigate = useNavigate(); // Use navigate for redirect
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

    // Toggle sidebar visibility
    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    // Fetch categories and user's products from the backend
    useEffect(() => {
        const fetchData = async () => {
            try {
                const token = localStorage.getItem('token');
                const userRole = localStorage.getItem('role'); // Make sure 'role' is set during login

                // Fetch categories (assuming this endpoint is open to both admin and user)
                const categoriesResponse = await axios.get(`${API_BASE_URL}/categories`);
                setCategories(categoriesResponse.data);

                // Conditionally fetch products based on the user role
                let productsEndpoint =
                    userRole === 'admin'
                        ? `${API_BASE_URL}/admin/products`
                        : `${API_BASE_URL}/user/products`;

                const productsResponse = await axios.get(productsEndpoint, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                setProducts(productsResponse.data);
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };
        fetchData();
    }, []);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            const decodedToken = jwtDecode(token); // Use jwtDecode function
            console.log('Decoded Token:', decodedToken);
            setIsAdmin(decodedToken.role === 'admin');
            console.log('Is Admin:', decodedToken.role === 'admin');
        }
    }, []);

    // Logout handler
    const handleLogout = () => {
        localStorage.removeItem('token'); // Clear token
        navigate('/login'); // Redirect to login
    };

    // Handle form submission for adding/updating a product
    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('token');
            const formData = new FormData();
            formData.append('name', productName);
            formData.append('description', productDescription);
            formData.append('category', selectedCategory);
            const attributes = attributesList.reduce((acc, attr) => {
                acc[attr.name] = attr.value;
                return acc;
            }, {});
            formData.append('attributes', JSON.stringify(attributes));

            if (imageFile) {
                formData.append('image_file', imageFile);
            } else if (imageUrl) {
                formData.append('image_url', imageUrl);
            }

            if (selectedProduct) {
                // Update existing product
                await axios.put(
                    `${API_BASE_URL}/user/products/${selectedProduct.id}`,
                    formData,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'multipart/form-data',
                        },
                    }
                );
                setMessage('Product updated successfully!');
            } else {
                // Add new product
                await axios.post(`${API_BASE_URL}/user/products`, formData, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data',
                    },
                });
                setMessage('Product added successfully!');
            }

            // Refresh the products list
            const productsResponse = await axios.get(`${API_BASE_URL}/user/products`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setProducts(productsResponse.data);

            // Clear the form
            resetForm();
        } catch (error) {
            console.error('Error adding/updating product:', error);
            setMessage('Failed to add/update product.');
        }
    };

    // Handle adding a new attribute
    const handleAddAttribute = () => {
        setAttributesList([...attributesList, { name: '', value: '' }]);
    };

    // Handle changing an attribute name or value
    const handleAttributeChange = (index, field, value) => {
        const updatedAttributes = [...attributesList];
        updatedAttributes[index][field] = value;
        setAttributesList(updatedAttributes);
    };

    // Handle removing an attribute
    const handleRemoveAttribute = (index) => {
        const updatedAttributes = attributesList.filter((_, idx) => idx !== index);
        setAttributesList(updatedAttributes);
    };

    // Handle editing a product
    const handleEdit = (product) => {
        setSelectedProduct(product);
        setProductName(product.name);
        setProductDescription(product.description);
        setSelectedCategory(product.category);
        const attributesArray = Object.entries(product.attributes || {}).map(([name, value]) => ({
            name,
            value,
        }));
        setAttributesList(attributesArray);
        setImageFile(null);
        setImageUrl('');
        setView('addProduct');
    };

    // Reset form
    const resetForm = () => {
        setSelectedProduct(null);
        setProductName('');
        setProductDescription('');
        setSelectedCategory('');
        setAttributesList([]);
        setImageFile(null);
        setImageUrl('');
        setMessage('');
        setView('products');
    };

    // Handle deleting a product
    const handleDelete = async (productId) => {
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API_BASE_URL}/user/products/${productId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setMessage('Product deleted successfully!');

            // Refresh the products list
            setProducts(products.filter((product) => product.id !== productId));
        } catch (error) {
            console.error('Error deleting product:', error);
            setMessage('Failed to delete product.');
        }
    };

    // Render products grid
    const renderProducts = () => (
        <div className="products-section">
            <h2 className='product-section-name'>Your Products</h2>
            {message && <p className="message">{message}</p>}
            <button onClick={() => setView('addProduct')} className="add-product-btn">
                Add New Product
            </button>
            <div className="product-cards">
                {products.map((product) => (
                    <div key={product.id} className="product-card">
                        <div className="product-header">
                            <h4>{product.name}</h4>
                            <span>{product.category}</span>
                        </div>
                        <img
                            src={product.image || '/path/to/default-image.jpg'}
                            alt={product.name}
                            className="product-image"
                        />
                        <div className="product-body">
                            <p>{product.description}</p>
                            <p>
                                <strong>Attributes:</strong>
                            </p>
                            <div className="product-attributes">
                                {Object.entries(product.attributes || {}).map(([key, value], index) => (
                                    <div key={index} className="attribute-badge">
                                        {key}: {value}
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="product-footer">
                            <button className="edit-button" onClick={() => handleEdit(product)}>
                                Edit
                            </button>
                            <button className="delete-button" onClick={() => handleDelete(product.id)}>
                                Delete
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    // Render add/update product form
    const renderAddProductForm = () => (
        <div className="add-product-section">
            <h2>{selectedProduct ? 'Update Product' : 'Add a New Product'}</h2>
            {message && <p className="message">{message}</p>}
            <form onSubmit={handleSubmit} encType="multipart/form-data">
                <input
                    type="text"
                    placeholder="Product Name"
                    value={productName}
                    onChange={(e) => setProductName(e.target.value)}
                    required
                />
                <textarea
                    placeholder="Product Description"
                    value={productDescription}
                    onChange={(e) => setProductDescription(e.target.value)}
                    required
                ></textarea>
                <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    required
                >
                    <option value="">-- Select Category --</option>
                    {categories.map((category, index) => (
                        <option key={index} value={category}>
                            {category}
                        </option>
                    ))}
                </select>
                <div className="attributes-section">
                    <h4>Product Attributes</h4>
                    {attributesList.map((attribute, index) => (
                        <div key={index} className="attribute-row">
                            <input
                                type="text"
                                placeholder="Attribute Name"
                                value={attribute.name}
                                onChange={(e) => handleAttributeChange(index, 'name', e.target.value)}
                                required
                            />
                            <input
                                type="text"
                                placeholder="Attribute Value"
                                value={attribute.value}
                                onChange={(e) => handleAttributeChange(index, 'value', e.target.value)}
                                required
                            />
                            <button type="button" onClick={() => handleRemoveAttribute(index)}>
                                Remove
                            </button>
                        </div>
                    ))}
                    <button type="button" onClick={handleAddAttribute} className="add-attribute-btn">
                        Add Attribute
                    </button>
                </div>
                <div className="image-upload">
                    <h4>Product Image</h4>
                    <input type="file" accept="image/*" onChange={(e) => setImageFile(e.target.files[0])} />
                    <p>Or</p>
                    <input
                        type="text"
                        placeholder="Image URL"
                        value={imageUrl}
                        onChange={(e) => setImageUrl(e.target.value)}
                    />
                </div>
                <div className="form-buttons">
                    <button type="submit">{selectedProduct ? 'Update Product' : 'Add Product'}</button>
                    <button type="button" onClick={resetForm} className="cancel-btn">
                        Cancel
                    </button>
                </div>
            </form>
        </div>
    );

    return (
        <div className="user-dashboard">
            {/* Sidebar Toggle Button */}
            <button className="toggle-sidebar" onClick={toggleSidebar}>
                <FontAwesomeIcon icon={faBars} />
            </button>

            {/* Sidebar */}
            <div className={`sidebar ${isSidebarOpen ? '' : 'hidden'}`}>
                <h2>User Panel</h2>
                <ul className="sidebar-menu">
                    <li onClick={() => setView('dashboard')}>
                        <i className="fas fa-tachometer-alt"></i> Dashboard
                    </li>
                    <li onClick={() => setView('categories')}>
                        <i className="fas fa-tags"></i> Categories
                    </li>
                    <li onClick={() => setView('products')}>
                        <i className="fas fa-box"></i> Products
                    </li>
                    <li onClick={() => setView('reportForm')}>
                        <i className="fas fa-exclamation-circle"></i> Report Issue
                    </li>
                    <li onClick={() => setView('userReports')}>
                        <i className="fas fa-file-alt"></i> Your Reports
                    </li>
                    <li onClick={() => setView('messages')}>
                        <i className="fas fa-envelope"></i> Messages
                    </li>
                    <li onClick={() => setView('inventoryCheck')}>
                        <i className="fas fa-clipboard-check"></i> Inventory Check
                    </li>
                    <li onClick={() => setView('orders')}>
                        <FontAwesomeIcon icon={faClipboardList} /> Orders
                    </li>
                    <li onClick={() => setView('calendar')}>
        <FontAwesomeIcon icon={faClipboardList} /> Calendar
    </li>
                    <li onClick={handleLogout}>
                        <FontAwesomeIcon icon={faSignOutAlt} /> Logout
                    </li>
                </ul>
            </div>

            {/* Main Content */}
            <div className="main-content">
                {view === 'dashboard' && (
                    <div className="dashboard-section">
                        <h2>Welcome to your user dashboard!</h2>
                        <p>Here you can manage your products, view categories, send messages, and more.</p>
                        <UserDashboardChart
                            data={{
                                labels: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
                                datasets: [
                                    {
                                        label: 'Weekly Activity',
                                        data: [12, 19, 3, 5, 2, 3, 7],
                                        borderColor: '#4a69bd',
                                        backgroundColor: 'rgba(74, 105, 189, 0.2)',
                                        fill: true,
                                        tension: 0.4,
                                    },
                                ],
                            }}
                            options={{
                                responsive: true,
                                plugins: {
                                    legend: { display: true, position: 'top' },
                                },
                            }}
                        />
                    </div>
                )}

                {view === 'categories' && (
                    <div className="categories-section">
                        <h2>Categories</h2>
                        <div className="category-cards">
                            {categories.map((category, index) => (
                                <div key={index} className="category-card">
                                    <FontAwesomeIcon
                                        icon={
                                            category === 'Electronics'
                                                ? faLaptop
                                                : category === 'Mobile'
                                                ? faMobileAlt
                                                : category === 'Books'
                                                ? faBook
                                                : category === 'Appliances'
                                                ? faTv
                                                : faCoffee // Default icon
                                        }
                                        className="category-icon"
                                    />
                                    <div className="category-title">{category}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
    {view === 'calendar' && <CalendarView />}
                {view === 'inventoryCheck' && <InventoryCheck />}
                {view === 'products' && renderProducts()}
                {view === 'orders' && <Orders isAdmin={isAdmin} />}
                {view === 'addProduct' && renderAddProductForm()}

                {view === 'reportForm' && (
                    <div className="report-form-section">
                        <ReportForm />
                    </div>
                )}

                {view === 'userReports' && (
                    <div className="user-reports-section">
                        <UserReports />
                    </div>
                )}

                {view === 'messages' && (
                    <div className="messages-section">
                        <Inbox />
                        <SendMessage />
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserPage;
